import { LevelDesignerElementBase } from '../elements/element-base';
import { LevelDesignerComponent } from '../level-designer/level-designer.component';
import { EmbossedRectangle } from '../utils/embossed-rectangle';
import { MouseEventResult } from '../managers/manager-base';

interface ValidationError {
    element: LevelDesignerElementBase;
    errors: string[];
}

export class ValidationPanel {
    private ctx: CanvasRenderingContext2D;
    private width: number = 400;
    private height: number;
    private x: number;
    private y: number = 60; // Below toolbar
    private isExpanded: boolean = false;
    private animationStartTime: number | null = null;
    private animationProgress: number = 0; // 0 to 1
    private headerHeight: number = 40; // Match toolbar height
    private rowHeight: number = 25;
    private scrollPosition: number = 0;
    private totalContentHeight: number = 0; // Store total content height for reuse
    private errors: ValidationError[] = [];
    private maxHeight: number = 300; // Maximum height before scrolling
    private expandedHeight: number = 0; // Will be calculated based on content

    constructor(
        private levelDesigner: LevelDesignerComponent,
        private canvas: HTMLCanvasElement
    ) {
        this.ctx = canvas.getContext('2d')!;
        this.height = this.headerHeight; // Start with header height
        this.x = (canvas.width - this.width) / 2; // Center horizontally
    }

    public updateErrors(errors: Map<LevelDesignerElementBase, string[]>) {
        this.errors = Array.from(errors.entries()).map(([element, errors]) => ({
            element,
            errors
        }));
        
        // Calculate total content height needed
        let contentHeight = 10;
        const textMargin = 10;
        const buttonWidth = 40;
        const buttonHeight = 20;
        const buttonMargin = 10;
        const maxTextWidth = this.width - buttonWidth - buttonMargin * 3 - textMargin * 2;

        this.errors.forEach(error => {
            contentHeight += this.rowHeight; // Element type header
            
            error.errors.forEach(errorMessage => {
                // Calculate height needed for wrapped error message
                const wrappedLines = this.wrapText(errorMessage, maxTextWidth);
                const lineHeight = 14;
                const messageHeight = Math.max(this.rowHeight, wrappedLines.length * lineHeight + 10);
                contentHeight += messageHeight;
            });
        });

        // Store the total content height for reuse
        this.totalContentHeight = contentHeight;

        // Set expanded height to either the content height or max height, whichever is smaller
        this.expandedHeight = Math.min(contentHeight + this.headerHeight, this.maxHeight);
    }

    private wrapText(text: string, maxWidth: number): string[] {
        const words = text.split(' ');
        const lines: string[] = [];
        let currentLine = words[0];

        for (let i = 1; i < words.length; i++) {
            const width = this.ctx.measureText(currentLine + ' ' + words[i]).width;
            if (width < maxWidth) {
                currentLine += ' ' + words[i];
            } else {
                lines.push(currentLine);
                currentLine = words[i];
            }
        }
        lines.push(currentLine);

        return lines;
    }

    public draw() {
        if(this.errors.length === 0) {
            return;
        }
        // Calculate current height based on animation progress
        const collapsedHeight = this.headerHeight;
        this.height = collapsedHeight + (this.expandedHeight - collapsedHeight) * this.animationProgress;

        // Create gradient for header background
        const gradient = this.ctx.createLinearGradient(this.x, this.y, this.x, this.y + this.headerHeight);
        gradient.addColorStop(0, '#E8E8E8');
        gradient.addColorStop(1, '#C8C8C8');

        // Draw header with gradient and embossed effect
        EmbossedRectangle.draw(this.ctx, this.x, this.y, this.width, this.headerHeight, {
            topLeft: 5,
            topRight: 5,
            bottomRight: this.isExpanded ? 0 : 5,
            bottomLeft: this.isExpanded ? 0 : 5
        }, gradient);

        // Draw header text
        this.ctx.fillStyle = '#333333';
        this.ctx.font = 'bold 12px Arial';
        this.ctx.textAlign = 'left';
        this.ctx.textBaseline = 'middle';
        const errorCount = this.errors.reduce((sum, error) => sum + error.errors.length, 0);
        this.ctx.fillText(`${errorCount} Validation Error${errorCount !== 1 ? 's' : ''}`, this.x + 10, this.y + this.headerHeight / 2);

        // Draw chevron
        const chevronX = this.x + this.width - 25;
        const chevronY = this.y + this.headerHeight / 2;
        const chevronSize = 8;
        
        this.ctx.beginPath();
        this.ctx.strokeStyle = '#666666';
        this.ctx.lineWidth = 2;
        
        if (!this.isExpanded) {
            // Draw chevron down
            this.ctx.moveTo(chevronX - chevronSize/2, chevronY - chevronSize/3);
            this.ctx.lineTo(chevronX, chevronY + chevronSize/3);
            this.ctx.lineTo(chevronX + chevronSize/2, chevronY - chevronSize/3);
        } else {
            // Draw chevron up
            this.ctx.moveTo(chevronX - chevronSize/2, chevronY + chevronSize/3);
            this.ctx.lineTo(chevronX, chevronY - chevronSize/3);
            this.ctx.lineTo(chevronX + chevronSize/2, chevronY + chevronSize/3);
        }
        this.ctx.stroke();

        // If not expanded or animating to collapsed, don't draw the rest
        if (this.animationProgress === 0) {
            return;
        }

        // Draw content with opacity based on animation progress
        this.ctx.globalAlpha = this.animationProgress;

        // Draw white background for entire content area first
        this.ctx.fillStyle = '#ffffff';
        this.ctx.fillRect(this.x, this.y + this.headerHeight, this.width, this.height - this.headerHeight);

        // Add a subtle border to separate content from header
        this.ctx.strokeStyle = '#e0e0e0';
        this.ctx.lineWidth = 1;
        this.ctx.strokeRect(this.x, this.y + this.headerHeight, this.width, this.height - this.headerHeight);

        // Save the current context state
        this.ctx.save();
        
        // Set up clipping region for content area
        this.ctx.beginPath();
        this.ctx.rect(this.x, this.y + this.headerHeight, this.width, this.height - this.headerHeight);
        this.ctx.clip();

        // Calculate content area dimensions
        let currentY = this.y + this.headerHeight + 5 - this.scrollPosition;
        const buttonWidth = 40;
        const buttonHeight = 20;
        const buttonMargin = 10;
        const textMargin = 10;
        const maxTextWidth = this.width - buttonWidth - buttonMargin * 3 - textMargin * 2;

        // Draw errors
        this.errors.forEach(error => {
            // Draw element type header
            this.ctx.fillStyle = '#e0e0e0';
            this.ctx.fillRect(this.x + 5, currentY, this.width - 10, this.rowHeight);
            
            this.ctx.fillStyle = '#333333';
            this.ctx.font = 'bold 11px Arial';
            this.ctx.fillText(error.element.TypeName, this.x + textMargin, currentY + this.rowHeight / 2);
            
            currentY += this.rowHeight;

            // Draw errors for this element type
            error.errors.forEach(errorMessage => {
                // Set font for measuring and drawing error text
                this.ctx.font = '11px Arial';
                
                // Wrap the error message
                const wrappedLines = this.wrapText(errorMessage, maxTextWidth);
                const lineHeight = 14; // Height for each line of wrapped text
                const totalHeight = Math.max(this.rowHeight, wrappedLines.length * lineHeight + 10);
                
                // Draw error row background
                this.ctx.fillStyle = '#ffffff';
                this.ctx.fillRect(this.x + 5, currentY, this.width - 10, totalHeight);
                
                // Draw error message lines
                this.ctx.fillStyle = '#ff0000';
                wrappedLines.forEach((line, index) => {
                    this.ctx.fillText(line, this.x + textMargin, currentY + 10 + index * lineHeight);
                });
                
                // Draw view button
                const buttonX = this.x + this.width - buttonWidth - buttonMargin;
                const buttonY = currentY + (totalHeight - buttonHeight) / 2;
                
                this.ctx.fillStyle = '#007bff';
                this.ctx.fillRect(buttonX, buttonY, buttonWidth, buttonHeight);
                
                this.ctx.fillStyle = '#ffffff';
                this.ctx.font = '10px Arial';
                this.ctx.textAlign = 'center';
                this.ctx.fillText('View', buttonX + buttonWidth / 2, buttonY + buttonHeight / 2 + 1);
                this.ctx.textAlign = 'left';
                
                currentY += totalHeight;
            });
        });

        // Restore the context state (removes clipping)
        this.ctx.restore();

        // Update total content height
        this.totalContentHeight = currentY + this.scrollPosition - this.y;

        // Draw scroll indicators if content is scrollable
        if (this.totalContentHeight > this.height) {
            const indicatorWidth = 4;
            const scrollbarHeight = (this.height / this.totalContentHeight) * this.height;
            const scrollbarY = this.y + (this.scrollPosition / this.totalContentHeight) * this.height;
            
            // Draw scroll track
            this.ctx.fillStyle = '#e0e0e0';
            this.ctx.fillRect(this.x + this.width - indicatorWidth - 2, this.y, indicatorWidth, this.height);
            
            // Draw scroll thumb
            this.ctx.fillStyle = '#999999';
            this.ctx.fillRect(this.x + this.width - indicatorWidth - 2, scrollbarY, indicatorWidth, scrollbarHeight);
        }

        this.ctx.globalAlpha = 1;
    }

    public handleClick(event: MouseEvent): boolean {
        const rect = this.canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // Use max height for hit detection if expanded, otherwise just header height
        const currentHeight = this.isExpanded ? this.maxHeight : this.headerHeight;

        // Check if click is within the panel bounds using current height
        if (x < this.x || x > this.x + this.width || y < this.y || y > this.y + currentHeight) {
            return false;
        }

        // Check if click is in the header area
        if (y >= this.y && y <= this.y + this.headerHeight) {
            // Toggle expand/collapse when clicking anywhere in the header
            this.toggleExpand();
            return true;
        }

        // If not expanded, don't handle any other clicks
        if (!this.isExpanded || this.animationProgress === 0) {
            return true;
        }

        let currentY = this.y + this.headerHeight + 5 - this.scrollPosition;

        // Check for clicks on view buttons
        this.errors.forEach(error => {
            currentY += this.rowHeight; // Skip element type header

            error.errors.forEach(errorMessage => {
                const buttonWidth = 40;
                const buttonHeight = 20;
                const buttonMargin = 10;
                const textMargin = 10;
                const maxTextWidth = this.width - buttonWidth - buttonMargin * 3 - textMargin * 2;
                
                // Calculate height needed for wrapped error message
                const wrappedLines = this.wrapText(errorMessage, maxTextWidth);
                const lineHeight = 14;
                const messageHeight = Math.max(this.rowHeight, wrappedLines.length * lineHeight + 10);
                
                const buttonX = this.x + this.width - buttonWidth - buttonMargin;
                const buttonY = currentY + (messageHeight - buttonHeight) / 2;

                if (x >= buttonX && x <= buttonX + buttonWidth &&
                    y >= buttonY && y <= buttonY + buttonHeight) {
                    // Find and select the element with this error
                    if (error.element) {
                        this.levelDesigner.setSelectedElements([error.element]);
                        
                        // Get the element's bounding box
                        const boundingBox = error.element.getBoundingBox();
                        
                        // Calculate the scale to fit the element in two-thirds of the view
                        const canvasWidth = this.canvas.width;
                        const canvasHeight = this.canvas.height;
                        const targetWidth = canvasWidth * 0.1;
                        const targetHeight = canvasHeight * 0.1;
                        
                        const scaleX = targetWidth / boundingBox.width;
                        const scaleY = targetHeight / boundingBox.height;
                        const newScale = Math.min(scaleX, scaleY, 10);
                        
                        // Set the new scale
                        this.levelDesigner.setScale(newScale);
                        
                        // Calculate new offset to center the element
                        const centerX = boundingBox.x + boundingBox.width / 2;
                        const centerY = boundingBox.y + boundingBox.height / 2;
                        this.levelDesigner.setOffsetX(canvasWidth / 2 - centerX * newScale);
                        this.levelDesigner.setOffsetY(canvasHeight / 2 - centerY * newScale);
                        
                        // Collapse the validation panel
                        this.isExpanded = false;
                        this.animationStartTime = null;
                        requestAnimationFrame(this.handleAnimation.bind(this));
                    }
                    return true;
                }

                currentY += messageHeight;
                return false;
            });
        });

        // If we get here, the click was in the panel but not on any interactive elements
        // Return true to prevent event propagation
        return true;
    }

    public handleWheel(event: WheelEvent): boolean {
        const rect = this.canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        if (!this.isPointInside(x, y) || !this.isExpanded || this.animationProgress === 0) {
            return false;
        }

        event.preventDefault();

        // Calculate new scroll position
        const scrollSpeed = 30;
        this.scrollPosition += event.deltaY > 0 ? scrollSpeed : -scrollSpeed;

        // Clamp scroll position
        const maxScroll = Math.max(0, this.totalContentHeight - this.height);
        this.scrollPosition = Math.max(0, Math.min(this.scrollPosition, maxScroll));

        this.levelDesigner.redraw();
        return true;
    }

    private handleAnimation(currentTime: number) {
        if (this.animationStartTime === null) {
            this.animationStartTime = currentTime;
        }

        const elapsed = currentTime - this.animationStartTime;
        const duration = 500; // 500ms animation duration
        
        if (elapsed < duration) {
            this.animationProgress = this.isExpanded ? 
                elapsed / duration :        // Expanding
                1 - (elapsed / duration);   // Collapsing
            
            // Use easeInOutCubic for smooth animation
            const t = this.animationProgress;
            this.animationProgress = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            
            requestAnimationFrame(this.handleAnimation.bind(this));
        } else {
            this.animationProgress = this.isExpanded ? 1 : 0;
            this.animationStartTime = null;
        }
        
        this.levelDesigner.redraw();
    }

    private toggleExpand() {
        this.isExpanded = !this.isExpanded;
        this.animationStartTime = null;
        requestAnimationFrame(this.handleAnimation.bind(this));
    }

    public isPointInside(x: number, y: number): boolean {
        // Calculate current height based on animation progress
        const collapsedHeight = this.headerHeight;
        const currentHeight = collapsedHeight + (this.expandedHeight - collapsedHeight) * this.animationProgress;
        
        return x >= this.x && x <= this.x + this.width &&
               y >= this.y && y <= this.y + currentHeight;
    }

    public setPosition(x: number, y: number) {
        this.x = x;
        this.y = y;
    }

    public setDimensions(width: number, height: number) {
        this.width = width;
        this.headerHeight = height;
    }

    public getWidth(): number {
        return this.width;
    }

    handleDoubleClick(x: number, y: number): MouseEventResult | undefined {
        // Check if click is inside the panel area
        if (this.isPointInside(x, y)) {
            return {
                cursor: 'default',
                stopPropagation: true,
                redraw: false
            };
        }
        return undefined;
    }
} 
